import { KK, VK } from '@/define/define'
import { kbVKTexture } from '@/define/vkcode'
import { DeviceManager } from '@/service/device'
import { getCurrentDevice } from '@/profiles/selector'
import { setData } from '@/service/driver/data'
import { ImageLightFitMode, ImageLightOptions } from './types'
import { resolveImageRect } from './image_rect'
import { imageLightHidWriter } from './hid_writer'
import { lightMatrix } from '@hid/define/define_light_matrix'
import { JingTaiV2Devices, type DefineDevice, type KK as HidKK, type VK as HidVK } from '@hid/define/define'
import { GetDevicesEnbleKeys, HidFromKK, HIDFromVk } from '@hid/define/define_fn'

export type ImageLightSource = CanvasImageSource & {
  width?: number
  height?: number
  displayWidth?: number
  displayHeight?: number
}

export interface ImageLightPreviewKey {
  kk: KK
  vk: VK
  label: string
  x: number
  y: number
  w: number
  h: number
}

export interface ImageLightPreviewLayout {
  width: number
  height: number
  keys: ImageLightPreviewKey[]
}

/**
 * The image is sampled against the key area, not the virtual keyboard frame.
 * This describes that same key area in the stage's untransformed CSS pixels so
 * callers can keep the rendered image aligned when an ancestor uses scale().
 */
export interface ImageLightRenderedKeyViewport {
  x: number
  y: number
  width: number
  height: number
}

const clamp = (value: number, min: number, max: number) => Math.min(max, Math.max(min, value))

const splitRgb = (value: number) => ({
  r: (value >> 16) & 0xff,
  g: (value >> 8) & 0xff,
  b: value & 0xff,
})

const toRgbNumber = (r: number, g: number, b: number) => {
  return (clamp(Math.round(r), 0, 255) << 16) | (clamp(Math.round(g), 0, 255) << 8) | clamp(Math.round(b), 0, 255)
}

/** Convert an sRGB channel to linear light before blending or averaging. */
const srgbToLinear = (value: number) => {
  const channel = clamp(value, 0, 255) / 255
  return channel <= 0.04045 ? channel / 12.92 : Math.pow((channel + 0.055) / 1.055, 2.4)
}

/** Convert a linear-light channel back to the sRGB value expected by the LED driver. */
const linearToSrgb = (value: number) => {
  const channel = clamp(value, 0, 1)
  return (channel <= 0.0031308 ? channel * 12.92 : 1.055 * Math.pow(channel, 1 / 2.4) - 0.055) * 255
}

const getKeyLabel = (vk: VK) => {
  const label = getCurrentDevice.value.texture[vk]?.text || kbVKTexture[vk] || ''
  return label.replace(/\n/g, ' ')
}

const collectPreviewKeys = (): ImageLightPreviewKey[] => {
  const device = getCurrentDevice.value
  const keys: ImageLightPreviewKey[] = []
  let cursorY = 0

  device.getKeyDataAndLayout().forEach((row) => {
    let cursorX = 0
    let rowHeight = 0

    row.forEach((vk, kk) => {
      const keySquare = device.getKeySquare(kk)
      const x = device.isAbsolute(kk) ? keySquare.x : cursorX + keySquare.x
      const y = device.isAbsolute(kk) ? keySquare.y : cursorY + keySquare.y
      const w = Math.max(1, keySquare.w)
      const h = Math.max(1, keySquare.h)

      keys.push({
        kk,
        vk,
        label: getKeyLabel(vk),
        x,
        y,
        w,
        h,
      })

      if (!device.isAbsolute(kk)) {
        cursorX += keySquare.x + w
        rowHeight = Math.max(rowHeight, keySquare.y + h)
      }
    })

    cursorY += rowHeight
  })

  return keys
}

export const getImageLightPreviewLayout = (): ImageLightPreviewLayout => {
  // Never derive the color matrix from DOM geometry.  The virtual device can be
  // resized with CSS zoom/scale (and browsers round transformed pixels
  // differently per key), which must not alter the colors sent to the device.
  // The device definition is the stable, physical coordinate system.
  const keys = collectPreviewKeys()

  if (keys.length === 0) {
    return { width: 1, height: 1, keys: [] }
  }

  const minX = Math.min(...keys.map((key) => key.x))
  const minY = Math.min(...keys.map((key) => key.y))
  const maxX = Math.max(...keys.map((key) => key.x + key.w))
  const maxY = Math.max(...keys.map((key) => key.y + key.h))

  return {
    width: Math.max(1, maxX - minX),
    height: Math.max(1, maxY - minY),
    keys: keys.map((key) => ({
      ...key,
      x: key.x - minX,
      y: key.y - minY,
    })),
  }
}

export const getImageLightRenderedKeyViewport = (stage: HTMLElement): ImageLightRenderedKeyViewport | null => {
  const stageRect = stage.getBoundingClientRect()
  const stageWidth = stage.clientWidth
  const stageHeight = stage.clientHeight
  if (!stageWidth || !stageHeight || !stageRect.width || !stageRect.height) {
    return null
  }

  const keyRects = Array.from(stage.querySelectorAll<HTMLElement>('#virtual_device .device_key'))
    .map((key) => key.getBoundingClientRect())
    .filter((rect) => rect.width > 0 && rect.height > 0)

  if (keyRects.length === 0) {
    return null
  }

  const left = Math.min(...keyRects.map((rect) => rect.left))
  const top = Math.min(...keyRects.map((rect) => rect.top))
  const right = Math.max(...keyRects.map((rect) => rect.right))
  const bottom = Math.max(...keyRects.map((rect) => rect.bottom))
  const scaleX = stageWidth / stageRect.width
  const scaleY = stageHeight / stageRect.height

  return {
    x: (left - stageRect.left) * scaleX,
    y: (top - stageRect.top) * scaleY,
    width: (right - left) * scaleX,
    height: (bottom - top) * scaleY,
  }
}

const getImageSourceSize = (source: ImageLightSource) => {
  return {
    width: source.displayWidth || source.width || 1,
    height: source.displayHeight || source.height || 1,
  }
}

export const mapImageBitmapToKeyColors = (bitmap: ImageLightSource, options: ImageLightOptions): Record<number, number> => {
  const layout = getImageLightPreviewLayout()
  if (layout.keys.length === 0) {
    return {}
  }

  const canvas = document.createElement('canvas')
  canvas.width = Math.max(1, Math.ceil(layout.width))
  canvas.height = Math.max(1, Math.ceil(layout.height))

  const ctx = canvas.getContext('2d', { willReadFrequently: true })
  if (!ctx) {
    return {}
  }

  const bg = splitRgb(options.backgroundColor)
  ctx.fillStyle = `rgb(${bg.r}, ${bg.g}, ${bg.b})`
  ctx.fillRect(0, 0, canvas.width, canvas.height)

  const sourceSize = getImageSourceSize(bitmap)
  const rect = resolveImageRect(sourceSize.width, sourceSize.height, canvas.width, canvas.height, options)
  ctx.drawImage(bitmap, rect.x, rect.y, rect.width, rect.height)

  const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height)
  const colors: Record<number, number> = {}
  const brightness = clamp(options.brightness, 0, 100) / 100
  const bgLinear = {
    r: srgbToLinear(bg.r),
    g: srgbToLinear(bg.g),
    b: srgbToLinear(bg.b),
  }

  layout.keys.forEach((key) => {
    // A physical key has one LED close to its centre. Sampling the complete
    // visual key cap causes anti-aliased edges and neighbouring highlights to
    // leak into adjacent keys, especially when the virtual keyboard is scaled.
    // Sample a little over the centre half, with a stronger centre weight: this
    // keeps the LED placement believable without washing out local colour.
    const sampleWidth = Math.max(1, key.w * 0.52)
    const sampleHeight = Math.max(1, key.h * 0.52)
    const centerX = key.x + key.w / 2
    const centerY = key.y + key.h / 2
    const sampleStep = Math.max(1, Math.floor(Math.min(sampleWidth, sampleHeight) / 5))
    const minX = clamp(Math.floor(centerX - sampleWidth / 2), 0, canvas.width - 1)
    const maxX = clamp(Math.ceil(centerX + sampleWidth / 2), 0, canvas.width)
    const minY = clamp(Math.floor(centerY - sampleHeight / 2), 0, canvas.height - 1)
    const maxY = clamp(Math.ceil(centerY + sampleHeight / 2), 0, canvas.height)
    let totalR = 0
    let totalG = 0
    let totalB = 0
    let totalWeight = 0

    for (let y = minY; y < maxY; y += sampleStep) {
      for (let x = minX; x < maxX; x += sampleStep) {
        const offset = (y * canvas.width + x) * 4
        const alpha = imageData.data[offset + 3] / 255
        const r = srgbToLinear(imageData.data[offset]) * alpha + bgLinear.r * (1 - alpha)
        const g = srgbToLinear(imageData.data[offset + 1]) * alpha + bgLinear.g * (1 - alpha)
        const b = srgbToLinear(imageData.data[offset + 2]) * alpha + bgLinear.b * (1 - alpha)
        const dx = (x + 0.5 - centerX) / (sampleWidth / 2)
        const dy = (y + 0.5 - centerY) / (sampleHeight / 2)
        const distance = Math.min(1, Math.sqrt(dx * dx + dy * dy))
        const weight = 1 + (1 - distance) * 1.5

        totalR += r * weight
        totalG += g * weight
        totalB += b * weight
        totalWeight += weight
      }
    }

    if (totalWeight === 0) {
      const px = clamp(Math.round(key.x + key.w / 2), 0, canvas.width - 1)
      const py = clamp(Math.round(key.y + key.h / 2), 0, canvas.height - 1)
      const offset = (py * canvas.width + px) * 4
      const alpha = imageData.data[offset + 3] / 255
      totalR = srgbToLinear(imageData.data[offset]) * alpha + bgLinear.r * (1 - alpha)
      totalG = srgbToLinear(imageData.data[offset + 1]) * alpha + bgLinear.g * (1 - alpha)
      totalB = srgbToLinear(imageData.data[offset + 2]) * alpha + bgLinear.b * (1 - alpha)
      totalWeight = 1
    }

    colors[key.kk] = toRgbNumber(linearToSrgb(totalR / totalWeight) * brightness, linearToSrgb(totalG / totalWeight) * brightness, linearToSrgb(totalB / totalWeight) * brightness)
  })

  return colors
}

export const applyStaticImageColors = async (colors: Record<number, number>) => {
  const current = setData.getData()
  const oldColors = { ...(current.LightCustom || {}) }

  if (current.ColorsDynamic?.Run) {
    current.ColorsDynamic.Run = false
  }

  current.LightLastIsCustom = true
  current.LightCustom = { ...colors }
  const ok = await imageLightHidWriter.applyStaticColors(colors, current.LightMode.Light)
  if (!ok) {
    current.LightCustom = oldColors
    throw new Error('image_light_hid_authorize_required')
  }
}

export const keyColorsToDynamicFrame = (colors: Record<number, number>) => {
  const frame: number[] = []
  const device = DeviceManager.instance.state.currentPid as unknown as DefineDevice
  if (JingTaiV2Devices.includes(device)) {
    return keyColorsToJingtaiV2DynamicFrame(colors)
  }

  const keyMap = lightMatrix(device)
  const kkByLightValue = getDynamicFrameKeyMap(device)

  for (let index = 0; index < keyMap.size; index++) {
    const lightValue = keyMap.get(index)
    const kk = lightValue === undefined ? undefined : (kkByLightValue.hid.get(lightValue) ?? kkByLightValue.deviceKk.get(lightValue) ?? kkByLightValue.virtualKey.get(lightValue))
    const color = kk !== undefined ? colors[kk] || 0 : 0
    frame.push((color >> 16) & 0xff, (color >> 8) & 0xff, color & 0xff)
  }

  return frame
}

const getDynamicFrameKeyMap = (device: DefineDevice) => {
  const hid = new Map<number, KK>()
  const deviceKk = new Map<number, KK>()
  const virtualKey = new Map<number, KK>()
  const enabledKeys = GetDevicesEnbleKeys(device)

  enabledKeys.forEach((vk, kk) => {
    deviceKk.set(kk, kk)
    virtualKey.set(vk, kk)

    const hidKey = HIDFromVk(vk as HidVK)
    if (hidKey !== undefined) {
      hid.set(hidKey, kk)
    }

    try {
      hid.set(HidFromKK(kk as unknown as HidKK, device), kk)
    } catch {
      // Some device definitions intentionally omit HID conversion for non-standard keys.
    }
  })

  return { hid, deviceKk, virtualKey }
}

const keyColorsToJingtaiV2DynamicFrame = (colors: Record<number, number>) => {
  const rows = 6
  const cols = 21
  const frame = Array(rows * cols * 3).fill(0)

  Object.entries(colors).forEach(([kkValue, color]) => {
    const kk = Number(kkValue)
    const row = (kk >> 8) & 0xff
    const col = kk & 0xff
    if (row >= rows || col >= cols) {
      return
    }

    const offset = (row * cols + col) * 3
    frame[offset] = (color >> 16) & 0xff
    frame[offset + 1] = (color >> 8) & 0xff
    frame[offset + 2] = color & 0xff
  })

  return frame
}
